import React, { useCallback, useEffect, useState } from "react";
import { AllModules, ColDef } from "@ag-grid-enterprise/all-modules";
import { AgGridReact } from "@ag-grid-community/react";
import { useTemplateGridOptions } from "hooks/useTemplateGridOptions";
import { ProjectRun } from "types";
import { dateComparator, localTime, getTimeRange, runKey } from "utils/utils";
import { ApolloError } from "@apollo/client";
import RequestTimeHeader from "./RequestTimeHeader";
import { DateRangeFilterContext } from "utils/context";
import { getUnixTime } from "date-fns";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";

type Props = {
    setProjectRun: (s: ProjectRun) => void;
    projectHistory: ProjectRun[];
    loading: boolean;
    error: ApolloError | undefined;
};

export default function ResultsGrid({ setProjectRun, projectHistory, loading, error }: Props) {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const getSearchParam = () => {
        return searchParams.get(runKey);
    };
    const [gridOptions, gridApi] = useTemplateGridOptions(columnDefs, undefined, getSearchParam, true);

    const [filterRange, setFilterRange] = useState<[Date | null, Date | null]>([null, null]);
    useEffect(() => {
        if (gridApi) {
            if (loading) {
                gridApi.showLoadingOverlay();
            }

            if (!loading && projectHistory) {
                gridApi.hideOverlay();
                gridApi.setRowData(projectHistory);
            }
        }
    }, [gridApi, loading, projectHistory]);

    useEffect(() => {
        if (gridApi && gridOptions) {
            gridOptions.onSelectionChanged = (e) => {
                const selectedNodes = e.api.getSelectedNodes();
                if (selectedNodes && selectedNodes[0]) {
                    searchParams.set(runKey, btoa(selectedNodes[0].id));
                    setSearchParams(searchParams);
                    setProjectRun(selectedNodes[0].data);
                }
            };
        }
    }, [gridApi, gridOptions, searchParams, setProjectRun, setSearchParams]);

    useEffect(() => {
        if (gridOptions) {
            const getContextMenuItems = (params: any) => {
                // make sure the correct node is selected on left-click before
                // copying the link to clipboard.
                params.node.setSelected(true);
                return [
                    {
                        name: "Diagnose",
                        action: () => {
                            const timeRange = getTimeRange(params.node.data.createdOn as number);
                            navigate({
                                pathname: "/diagnostics",
                                search: `?${createSearchParams({
                                    [runKey]: btoa(params.node.id),
                                    range: btoa(JSON.stringify(timeRange))
                                })}`
                            });
                            // history.push({
                            //     pathname: "/diagnostics",
                            //     state: { id: params.node.id, range: timeRange, shouldQuery: true }
                            // });
                        },
                        icon: "<span class='bp3-icon-standard bp3-icon-diagnosis bp3-intent-primary'></span>"
                    },
                    // {
                    //     name: "Copy Link",
                    //     action: () => {
                    //         navigator.clipboard.writeText(window.location.href);
                    //         CopyToaster.show({
                    //             message: `Copied to Clipboard`,
                    //             intent: "success",
                    //             timeout: 1500
                    //         });
                    //     },
                    //     icon: "<span class='bp3-icon-standard bp3-icon-link bp3-intent-primary'></span>"
                    // },
                    "separator",
                    "copy",
                    "copyWithHeaders"
                ];
            };
            gridOptions.getContextMenuItems = getContextMenuItems;
        }
    }, [gridOptions, navigate, setProjectRun]);

    const isFilter = useCallback(() => {
        const [to, from] = [...filterRange];
        return !!to || !!from;
    }, [filterRange]);

    const filterPass = useCallback(
        (node) => {
            const [from, to] = [...filterRange];
            const inSeconds = node.data.createdOn / 1000;
            if ((from && inSeconds < getUnixTime(from)) || (to && inSeconds > getUnixTime(to))) {
                return false;
            }
            return true;
        },
        [filterRange]
    );

    useEffect(() => {
        if (gridOptions) {
            gridOptions.isExternalFilterPresent = isFilter;
            gridOptions.doesExternalFilterPass = filterPass;
        }
    }, [gridOptions, filterRange, isFilter, filterPass]);

    useEffect(() => {
        if (gridApi) {
            if (filterRange) {
                gridApi.onFilterChanged();
            }
        }
    }, [gridApi, filterRange]);

    return (
        <DateRangeFilterContext.Provider value={[filterRange, setFilterRange]}>
            <AgGridReact
                modules={AllModules}
                gridOptions={gridOptions}
                frameworkComponents={{
                    customRequestTime: RequestTimeHeader
                }}
            />
        </DateRangeFilterContext.Provider>
    );
}

const columnDefs: ColDef[] = [
    {
        colId: "id",
        headerName: "Id",
        field: "id",
        cellClass: "no-border",
        hide: true
    },
    {
        colId: "company",
        headerName: "Company",
        field: "companyName",
        cellClass: "no-border"

        // cellRenderer: "companyCellRenderer"
    },
    {
        colId: "status",
        headerName: "Status",
        field: "status",
        cellClass: "no-border",
        cellStyle: (params) => {
            //todo: move these to cellCallsRules
            if (params.value === "Completed") {
                return { color: "#F5F8FA", backgroundColor: "#15B371" };
            }
            if (params.value === "Failed") {
                return { color: "#F5F8FA", backgroundColor: "#F55656" };
            }
            if (params.value === "Running") {
                return { color: "#F5F8FA", backgroundColor: "#F29D49" };
            }
        }
    },
    {
        colId: "createdOn",
        headerName: "Requested Time",
        field: "createdOn",
        cellClass: "no-border",
        sort: "desc",
        comparator: dateComparator,
        headerComponent: "customRequestTime",
        valueFormatter: ({ data }) => {
            return data.createdOn ? localTime(data.createdOn) : "";
        }
    },
    {
        colId: "lastUpdatedOn",
        headerName: "Last Updated Time",
        field: "lastUpdatedOn",
        cellClass: "no-border",
        //todo: add date filtering
        menuTabs: [],
        comparator: dateComparator,
        valueFormatter: ({ data }) => {
            return data.lastUpdatedOn ? localTime(data.lastUpdatedOn) : "";
        }
    },

    {
        colId: "user",
        headerName: "User",
        field: "user.name",
        cellClass: "no-border",
        flex: 1
        // cellStyle: (params) => {
        //     return params.value ? null : { color: "#F5F8FA", backgroundColor: "#D9822B" };
        // },
    }

    // {
    //     colId: "nextRunTime",
    //     headerName: "Next Run Time",
    //     field: "nextRunTime",
    //     cellClass: "no-border",
    //     valueFormatter: ({ data }) => localTime(data.nextRunTime),
    //     flex: 1,
    // },
];
